import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { authFetch } from "../../Utils/apiClient";

const Team = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await authFetch("/user/allusers");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("API Response:", result);

        if (!result.status || !result.data) {
          throw new Error("Invalid API response structure");
        }

        // Filter out null entries and format data
        const validUsers = result.data.filter(user => user !== null);
        
        const formattedData = validUsers.map((user) => {
          // Get name from different sources based on user type
          let displayName = user.profileName || "N/A";
          let displayPhone = "N/A";

          // Try to get name and phone from role-specific data
          if (user.Role) {
            switch (user.Role.role) {
              case "seller":
                if (user.Seller) {
                  displayName = user.Seller.sellername || user.profileName || "N/A";
                  displayPhone = user.Seller.sellerphone || "N/A";
                }
                break;
              case "churchmember":
                if (user.ChurchMember) {
                  displayName = user.ChurchMember.fullname || user.profileName || "N/A";
                  displayPhone = user.ChurchMember.memberphone || "N/A";
                }
                break;
              case "organization":
                if (user.Organization) {
                  displayName = user.Organization.name || user.profileName || "N/A";
                  displayPhone = user.Organization.orgphone || "N/A";
                }
                break;
              case "student":
                if (user.Student) {
                  displayName = `${user.Student.legalfirstname || ""} ${user.Student.legalmiddlename || ""} ${user.Student.legallastname || ""}`.trim() || user.profileName || "N/A";
                  displayPhone = user.Student.studentphone || user.Student.studentcellphone || "N/A";
                }
                break;
              case "deliver":
                if (user.Deliver) {
                  displayName = user.Deliver.delivername || user.profileName || "N/A";
                  displayPhone = user.Deliver.deliverphone || "N/A";
                }
                break;
              default:
                // For other roles (like ayfa), use profileName
                displayName = user.profileName || "N/A";
                displayPhone = "N/A";
            }
          }

          return {
            id: user.id,
            name: displayName,
            phone: displayPhone,
            email: user.email || "N/A",
            country: user.country || "N/A",
            role: user.Role ? user.Role.role : "N/A",
            status: user.IsVerified ? "Active" : "Inactive", // Using IsVerified as status
            profileName: user.profileName || "N/A",
          };
        });

        // Sort by ID in descending order
        formattedData.sort((a, b) => b.id - a.id);

        console.log("Formatted Data:", formattedData);
        setData(formattedData);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError(error.message);
        Swal.fire({
          title: "Error!",
          text: `Failed to fetch user data: ${error.message}`,
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteClick = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this user!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        authFetch("/user/deleteuser", {
          method: "DELETE",
          body: JSON.stringify({ userId: id }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to delete user");
            }
            return response.json();
          })
          .then((responseData) => {
            // Check if the API response indicates success
            if (responseData.status && responseData.response_code === 200) {
              // Update data state by filtering out the deleted user
              setData(prevData => prevData.filter((item) => item.id !== id));
              Swal.fire("Deleted!", responseData.message || "The user has been deleted.", "success");
            } else {
              throw new Error(responseData.message || "Delete operation failed");
            }
          })
          .catch((error) => {
            console.error("Error deleting user:", error);
            Swal.fire(
              "Error!",
              `Failed to delete user: ${error.message}`,
              "error"
            );
          });
      }
    });
  };

  const handleStatusChange = (id, newStatus) => {
    const updatedData = data.map((item) => {
      if (item.id === id) {
        return { ...item, status: newStatus };
      }
      return item;
    });
    setData(updatedData);
    console.log("Status changed for id:", id, "New status:", newStatus);
  };

  const exportToPdf = () => {
    if (data.length === 0) {
      Swal.fire("Warning!", "No data to export.", "warning");
      return;
    }

    const doc = new jsPDF();
    doc.autoTable({
      head: [
        ["ID", "Name", "Phone Number", "Email", "Country", "Role", "Status"],
      ],
      body: data.map(({ id, name, phone, email, country, role, status }) => [
        id,
        name,
        phone,
        email,
        country,
        role,
        status,
      ]),
    });
    doc.save("user_data.pdf");
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "phone", headerName: "Phone Number", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "country", headerName: "Country", flex: 1 },
    { field: "role", headerName: "Role", flex: 1 },
    { field: "status", headerName: "Status", flex: 0.7 },
    {
      field: "action",
      headerName: "Actions",
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <Link
              to={`/user/view/${params.row.role.toLowerCase()}/${params.row.id}`}
            >
              <IconButton size="small">
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box m="20px">
        <Header title="User Management" subtitle="Managing the users" />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <div>Loading users...</div>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box m="20px">
        <Header title="User Management" subtitle="Managing the users" />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <div>Error: {error}</div>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="User Management" subtitle="Managing the users" />

      <Box
        m="10px 0 0 0"
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <Button
          variant="contained"
          onClick={exportToPdf}
          sx={{
            backgroundColor: "#4caf50",
            color: "white",
            fontSize: "10px",
            "&:hover": {
              backgroundColor: "#388e3c",
            },
          }}
        >
          Export as PDF ({data.length} users)
        </Button>
        <DataGrid
          rows={data}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default Team;